import {
  ColDef,
  GridApi,
  GridReadyEvent,
  ICellRendererFunc,
  ICellRendererParams,
} from '@ag-grid-community/core'
import { SetFilter } from '@ag-grid-enterprise/set-filter'
import _ from 'lodash-es'
import { ColumnFilterModel } from '../models/filter-editor.model'
import {
  ColumnField,
  FilterType,
  SetFilterOptionModel,
} from '../models/filter.model'
import { getOperators } from './filter-operator-utils'
import { getFilterValueInputType } from './filter-type-utils'

export interface GetColumnFilterModelsOptions {
  omitColumns: ColumnField[]
}

/**
 * Refreshes the set values for all set filters type columns in the grid.
 * Useful for Filter on Cell Functionality
 */
export function refreshSetValuesForAllSetFilterColumns(event: GridReadyEvent) {
  const { columnApi, api } = event
  columnApi
    .getAllColumns()
    ?.filter(col => col.getColDef().filter === 'agSetColumnFilter')
    ?.forEach(col => {
      const colId = col.getColId()
      ;(
        api.getFilterInstance(colId) as SetFilter<string>
      )?.refreshFilterValues()
    })
}

/**
 *
 */
export function getColumnFilterModels(
  gridApi: GridApi,
  options?: GetColumnFilterModelsOptions,
): ColumnFilterModel[] {
  const colDefs = gridApi.getColumnDefs()
  if (!colDefs || colDefs.length === 0) {
    return []
  }

  const columnFilters = _.flatMap(colDefs, colDef => {
    const columnFilter = getColumnFilterModel(colDef, gridApi, options)
    return columnFilter ? [columnFilter] : []
  })

  const sortedColumnFilters = _.sortBy(
    columnFilters,
    colFilter => colFilter.columnName,
  )

  return sortedColumnFilters
}

/**
 *
 */
function getColumnFilterModel(
  colDef: ColDef,
  gridApi: GridApi,
  options?: GetColumnFilterModelsOptions,
): ColumnFilterModel | null {
  const field = colDef.field
  if (!field || options?.omitColumns.includes(field)) {
    return null
  }

  const filterType = getFilterType(field, colDef, gridApi)
  if (!filterType) {
    return null
  }

  const operators = getOperators(colDef, gridApi)
  if (!operators) {
    return null
  }

  const filterValueInputs = new Map()
  operators.forEach(op => {
    filterValueInputs.set(op.type, getFilterValueInputType(filterType, op.type))
  })

  return {
    columnName: colDef.headerName ?? _.startCase(field),
    field,
    filterType,
    defaultOperator: undefined,
    operators,
    filterValueInputs,
  }
}

/**
 *
 */
function getFilterType(
  field: string,
  colDef: ColDef,
  gridApi: GridApi,
): FilterType | null {
  const filter = colDef.filter
  if (!filter) {
    return null
  }

  const colType = colDef.type as string
  const filterType = (filter as string) ?? colType

  switch (filterType) {
    case 'text':
    case 'textFilter':
      return { type: 'text' }
    case 'numberFilter':
      if (colType == 'Identifier') {
        return { type: 'identifier' }
      }
      return { type: 'number' }
    case 'agSetColumnFilter': {
      const options = getSetFilterOptions(field, gridApi, colDef)
      return { type: 'set', options }
    }
    case 'dateFilter':
      return { type: 'date' }
    case 'timeFilter':
      return { type: 'time' }
    case 'DateTime':
    case 'dateTimeFilter':
      return { type: 'datetime' }
    default:
      return null
  }
}

/**
 * Get all options for a set filter.
 */
async function getSetFilterOptions(
  field: string,
  gridApi: GridApi,
  colDef: ColDef,
): Promise<SetFilterOptionModel[]> {
  const filterInstance = gridApi.getFilterInstance(field) as SetFilter<string>
  if (!(filterInstance instanceof SetFilter)) {
    // Not a set filter instance? Just resolve an empty list.
    return Promise.resolve([])
  }

  const valueModel = filterInstance.getValueModel()
  if (valueModel) {
    await valueModel.refreshValues()
  }

  const allValues = filterInstance.getValues()
  return _.flatMap(allValues, val =>
    val
      ? [
          {
            key: val,
            cellRenderer: () => getSetValueFormattedOption(colDef, val),
          },
        ]
      : [],
  )
}

/**
 * Find the cell renderer to use to format the value for a set filter option.
 *
 * @internal
 */
export function getSetValueFormattedOption(colDef: ColDef, val: string) {
  let cellRenderer: ICellRendererFunc | undefined

  // If there's a filter param cellRenderer, the cellRenderer is a function
  if (typeof colDef.filterParams?.cellRenderer === 'function') {
    cellRenderer = colDef.filterParams.cellRenderer
  }

  // Next priority is checking the value formatter
  else if (typeof colDef.filterParams?.valueFormatter === 'function') {
    cellRenderer = colDef.filterParams.valueFormatter
  }

  // If colDef has a cellRenderer, the cellRenderer is a function
  else if (typeof colDef.cellRenderer === 'function') {
    cellRenderer = colDef.cellRenderer as ICellRendererFunc
  }

  if (cellRenderer) {
    return (
      cellRenderer({
        value: val,
      } as ICellRendererParams) + ''
    )
  }

  return val
}
